// REACT IMPORTS
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// COMPONENT IMPORTS
import InputField from "components/form/InputField";
import Button from "components/form/Button";
import { useAppDispatch } from "store/hooks";
import { passwordForgot, signUpDetailsGet } from "store/slices/authSlice";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import { decode, validateEmail } from "utils/HelperFunctions";
import { useTid } from "utils/testid";
import { activateOnKey } from "utils/activateOnKey";

/* ------------------------------------- LOGIN VIA CRED FORM ------------------------------------- */

const ForgotPasswordForm = () => {
  const tid = useTid();
  let navigate = useNavigate();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const dispatch = useAppDispatch();

  const initialVal = {
    email: "",
  };

  const [values, setValues] = useState<any>(initialVal);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isErr, setIsErr] = useState<boolean>(false);
  const [isFormatErr, setIsFormatErr] = useState<boolean>(false);
  const [isMultiple, setIsMultiple] = useState<boolean>(false);

  const onSentOtp = async (value: any) => {
    setIsLoading(true);
    try {
      let payload = { ...value };
      let res: any = await dispatch(passwordForgot(payload)).unwrap();
      if (res && res?.body?.success) {
        setIsErr(false);
        navigate(`/${cluster_id}/forgot-otp-verification`, {
          state: {
            email: values?.email,
            data: res?.body?.data?.getAccounts,
            isEmailMutiple: value.isEmailMutiple,
          },
        });
      } else {
        setIsErr(true);
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      setIsErr(true);
      console.log("Forgot Password err :-->", error?.message);
    }
  };

  const getAccounts = async () => {
    try {
      let payload = {
        ...values,
        clusterId: clusterId,
      };
      let res: any = await dispatch(signUpDetailsGet(payload)).unwrap();
      if (res && res?.body?.success) {
        if (res?.body?.data?.isEmailMutiple === "one") {
          await onSentOtp({
            ...values,
            isEmailMutiple: res?.body?.data?.isEmailMutiple,
            cluster_id: clusterId,
            accountId: res?.body?.data?.getAccounts?.[0]?.accountid,
          });
        } else if (res?.body?.data?.isEmailMutiple === "multiple") {
          setIsMultiple(true);
          setValues({ ...values, accountid: "" });
          setIsLoading(false);
        } else {
          setIsErr(true);
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.log("onSubmit error =====", error);
    }
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    if (!validateEmail(values?.email || "")) {
      setIsFormatErr(true);
      return;
    }
    setIsFormatErr(false);
    setIsLoading(true);
    if (isMultiple) {
      await onSentOtp({
        isEmailMutiple: "multiple",
        cluster_id: clusterId,
        ...values,
      });
    } else {
      await getAccounts();
    }
  };

  return (
    <div
      className="animate__animated animate__fadeIn w-full lg:max-w-[520px]"
      data-testid={tid("form-shell")}
    >
      <div className="lg:text-3xl text-xl font-bold -tracking-[0.6px] leading-normal text-txtPrimary">
        Forgot Password
      </div>
      <p className="lg:py-10 pt-6 pb-10 max-w-[520px] text-txtParagraph lg:text-base text-sm leading-normal font-normal">
        Enter your email to receive a verification code
      </p>
      <form onSubmit={onSubmit} noValidate data-testid={tid("forgot-password-form")}>
        <div className="lg:max-w-[520px] flex flex-col lg:gap-[60px] gap-10">
          <div className="flex flex-col w-full gap-6">
            <div className="flex flex-col gap-3">
              <InputField
                type="email"
                name="email"
                label="Email"
                data-testid={tid("email-input")}
                aria-label="Email address"
                placeholder="Enter your email address"
                value={values?.email}
                onChange={(e: any) => {
                  if (isFormatErr) {
                    setIsFormatErr(false);
                  }
                  setValues({
                    ...values,
                    email: e?.target?.value,
                  });
                }}
                _className="!font-medium"
              />
              {isFormatErr && (
                <div
                  className="flex items-center gap-[5px] px-[10px] py-3 bg-lightRedBg rounded select-none animate__animated animate__fadeIn"
                  role="alert"
                  data-testid={tid("email-format-alert")}
                >
                  <InfoRedCircleIcon aria-hidden="true" />
                  <div className="lg:text-base text-sm text-red leading-[20.83px]">
                    Please enter a valid email address.
                  </div>
                </div>
              )}
            </div>
            {isMultiple && (
              <InputField
                type="text"
                name="accountId"
                label="ACCOUNT ID"
                data-testid={tid("account-id-input")}
                aria-label="Account ID"
                placeholder="Enter account id"
                value={values?.accountId}
                onChange={(e: any) => {
                  setValues({
                    ...values,
                    accountid: e?.target?.value,
                  });
                }}
                _className="!font-medium"
              />
            )}
          </div>
          <div>
            <Button
              isLoading={isLoading}
              className="w-full"
              text="Continue"
              type="submit"
              data-testid={tid("continue-button")}
              aria-label="Continue password reset"
              variant="variable"
              isDisabled={
                isMultiple
                  ? values?.email === "" || values?.accountId === ""
                  : values?.email === ""
              }
            />
            {isErr && (
              <>
                <div
                  className="flex items-center gap-[5px] mt-3 px-[10px] py-3 bg-lightRedBg rounded select-none animate__animated animate__fadeIn"
                  role="alert"
                  data-testid={tid("email-not-recognized-alert")}
                >
                  <InfoRedCircleIcon aria-hidden="true" />
                  <div className="lg:text-base text-sm text-red leading-[20.83px]">
                    Email not recognized.
                  </div>
                </div>
              </>
            )}
            <div className="pt-4 lg:text-base text-sm text-blue hover:!text-btnPrimaryHover leading-normal font-medium flex justify-center">
              <div
                className="cursor-pointer"
                onClick={() => navigate(-1)}
                onKeyDown={activateOnKey(() => navigate(-1))}
                role="button"
                tabIndex={0}
                data-testid={tid("go-back-button")}
                aria-label="Go back"
              >
                Go back
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ForgotPasswordForm;
